'use client';

// Single source of truth for "which component renders block.type X on the
// storefront/admin canvas" — BlockRenderer.tsx looks types up here instead of
// a hand-written if-chain, so adding a new simple block type only touches
// this file (plus the admin palette/property-editor, which stay separate —
// see EditorSidebarLeft.tsx).
//
// Every entry is dynamic() (see PRODUCT_BLOCK_TYPES's comment below for why): a page's builder
// JSON only ever uses a handful of the 25+ registered types, and BlockRenderer.tsx renders every
// type through the same generic `<RegisteredBlock {...sharedProps} />` path (no per-type extra
// props), so nothing here needs to be a static import — that would just pull every block type's
// code into every page's bundle regardless of whether that page's content uses it. Confirmed live
// via Lighthouse's script coverage on shopstag-ci: ButtonBlock/ImageBlock/SliderBlock were ~99%
// unused bytes on the homepage before this change, because they were statically imported here
// while that specific page's content used none of them.

import dynamic from 'next/dynamic';
import type { ComponentType } from 'react';

const HeaderBlockLazy = dynamic(() => import('./blocks/HeaderBlock').then(m => m.HeaderBlock));
const TextBlockLazy = dynamic(() => import('./blocks/TextBlock').then(m => m.TextBlock));
const ButtonBlockLazy = dynamic(() => import('./blocks/ButtonBlock').then(m => m.ButtonBlock));
const IconBlockLazy = dynamic(() => import('./blocks/IconBlock').then(m => m.IconBlock));
const SeparatorBlockLazy = dynamic(() => import('./blocks/SeparatorBlock').then(m => m.SeparatorBlock));
const ContainerBlockLazy = dynamic(() => import('./blocks/ContainerBlock').then(m => m.ContainerBlock));
const RowBlockLazy = dynamic(() => import('./blocks/RowBlock').then(m => m.RowBlock));
const VideoBlockLazy = dynamic(() => import('./blocks/VideoBlock').then(m => m.VideoBlock));
const BreadcrumbsBlockLazy = dynamic(() => import('./blocks/BreadcrumbsBlock').then(m => m.BreadcrumbsBlock));
const SubcategoriesBlockLazy = dynamic(() => import('./blocks/SubcategoriesBlock').then(m => m.SubcategoriesBlock));
const CategoryHeaderBlockLazy = dynamic(() => import('./blocks/CategoryHeaderBlock').then(m => m.CategoryHeaderBlock));
const FaqBlockLazy = dynamic(() => import('./blocks/FaqBlock').then(m => m.FaqBlock));
const SimpleBlocksLazy = dynamic(() => import('./blocks/SimpleBlocks').then(m => m.SimpleBlocks));
const ImageBlockLazy = dynamic(() => import('./blocks/ImageBlock').then(m => m.ImageBlock));
const SliderBlockLazy = dynamic(() => import('./blocks/SliderBlock').then(m => m.SliderBlock));

// One component internally dispatching on block.type for a handful of
// related types — each type still gets its own registry entry, pointing at
// the same component reference.
const SIMPLE_BLOCK_TYPES = ['products', 'categories', 'contact-form', 'newsletter', 'cart', 'checkout', 'catalog-filters'];

// Ticket types are intentionally listed here as string literals rather than
// imported from TicketBlocks.tsx: a static import of even one named export
// pulls the whole module into this file's (eagerly-loaded) bundle, defeating
// the dynamic()-based code splitting below for pages that don't sell tickets.
export const TICKET_BLOCK_TYPES = [
  'ticket-name',
  'ticket-gallery',
  'ticket-description',
  'ticket-short-description',
  'ticket-price-range',
  'ticket-event-selector',
  'ticket-seat-map',
];

const TicketBlocksLazy = dynamic(() => import('./blocks/TicketBlocks').then(m => m.TicketBlocks));

// Same reasoning as TICKET_BLOCK_TYPES above: listed as string literals instead of importing
// PRODUCT_BLOCK_TYPES from ProductBlocks.tsx, which would statically pull in the whole
// ~2000-line module (14 product-detail block components) on every page, even ones with no
// product blocks (e.g. the homepage). DEMO_PRODUCT was split out to core/components/Builder/
// demoProduct.ts for the same reason — BlockRenderer.tsx needs it for the admin preview
// without forcing this module to load.
export const PRODUCT_BLOCK_TYPES = [
  'product-name',
  'product-price',
  'product-quantity',
  'product-gallery',
  'product-description-short',
  'product-description-long',
  'product-feature-bullets',
  'product-variants',
  'product-add-to-cart',
  'product-stock',
  'product-reviews',
  'product-rating-mini',
  'product-condition',
  'product-spec',
  'product-downloads',
  'product-leasenow-button',
  'product-leasenow-calculator',
];

const ProductBlocksLazy = dynamic(() => import('./blocks/ProductBlocks').then(m => m.ProductBlocks));
const StoreChromeBlocksLazy = dynamic(() => import('./blocks/StoreChromeBlocks').then(m => m.StoreChromeBlocks));

const AccountProfileLazy = dynamic(() => import('@/modules/auth/components/ClubAccountBlocks').then(m => m.AccountProfileBlock));
const AccountOrdersLazy = dynamic(() => import('@/modules/auth/components/ClubAccountBlocks').then(m => m.AccountOrdersBlock));
const AccountAddressesLazy = dynamic(() => import('@/modules/auth/components/ClubAccountBlocks').then(m => m.AccountAddressesBlock));
const ClubBenefitsLazy = dynamic(() => import('@/modules/auth/components/ClubAccountBlocks').then(m => m.ClubBenefitsBlock));

export const BLOCK_COMPONENTS: Record<string, ComponentType<any>> = {
  image: ImageBlockLazy,
  slider: SliderBlockLazy,
  header: HeaderBlockLazy,
  separator: SeparatorBlockLazy,
  text: TextBlockLazy,
  button: ButtonBlockLazy,
  icon: IconBlockLazy,
  container: ContainerBlockLazy,
  row: RowBlockLazy,
  video: VideoBlockLazy,
  breadcrumbs: BreadcrumbsBlockLazy,
  subcategories: SubcategoriesBlockLazy,
  'category-header': CategoryHeaderBlockLazy,
  faq: FaqBlockLazy,
  'hero-packshot': dynamic(() => import('./blocks/HeroPackshotBlock').then(m => m.HeroPackshotBlock)),
  'feature-cards': dynamic(() => import('./blocks/FeatureCardsBlock').then(m => m.FeatureCardsBlock)),
  'media-gallery': dynamic(() => import('./blocks/MediaGalleryBlock').then(m => m.MediaGalleryBlock)),
  testimonials: dynamic(() => import('./blocks/TestimonialsBlock').then(m => m.TestimonialsBlock)),
  'club-promo': dynamic(() => import('./blocks/ClubPromoBlock').then(m => m.ClubPromoBlock)),
  'auth-box': dynamic(() => import('./blocks/AuthBoxBlock').then(m => m.AuthBoxBlock)),
  'product-showroom': dynamic(() => import('./blocks/ProductShowroomBlock').then(m => m.ProductShowroomBlock)),
  download: dynamic(() => import('./blocks/DownloadBlock').then(m => m.DownloadBlock)),
  'blog-posts': dynamic(() => import('./blocks/BlogPostsBlock').then(m => m.BlogPostsBlock)),
  'pick-pack': dynamic(() => import('./blocks/PickPackBlock').then(m => m.PickPackBlock)),
  'html-embed': dynamic(() => import('./blocks/HtmlEmbedBlock').then(m => m.HtmlEmbedBlock)),
  'contact-hours': dynamic(() => import('./blocks/ContactHoursBlock').then(m => m.ContactHoursBlock)),
  'school-calendar': dynamic(() => import('./blocks/SchoolCalendarBlock').then(m => m.SchoolCalendarBlock)),
  'staff-directory': dynamic(() => import('./blocks/StaffDirectoryBlock').then(m => m.StaffDirectoryBlock)),
  'stay-search': dynamic(() => import('./blocks/StayBlocks').then(m => m.StaySearchBlock)),
  'stay-rooms': dynamic(() => import('./blocks/StayBlocks').then(m => m.StayRoomsBlock)),
  iframe: dynamic(() => import('./blocks/HtmlEmbedBlock').then(m => m.HtmlEmbedBlock)),
  'account-profile': AccountProfileLazy,
  'account-orders': AccountOrdersLazy,
  'account-addresses': AccountAddressesLazy,
  'club-benefits': ClubBenefitsLazy,
  'social-media-gallery': dynamic(() => import('./blocks/SocialMediaGalleryBlock').then(m => m.SocialMediaGalleryBlock)),
  'scan-play': dynamic(() => import('./blocks/ScanPlayBlock').then(m => m.ScanPlayBlock)),
  'scroll-story': dynamic(() => import('./blocks/ScrollStoryBlock').then(m => m.ScrollStoryBlock)),
  marquee: dynamic(() => import('./blocks/MarqueeBlock').then(m => m.MarqueeBlock)),
  'reveal-list': dynamic(() => import('./blocks/RevealListBlock').then(m => m.RevealListBlock)),
  'advent-calendar': dynamic(() => import('./blocks/AdventCalendarBlock').then(m => m.AdventCalendarBlock)),
  countdown: dynamic(() => import('./blocks/CountdownBlock').then(m => m.CountdownBlock)),
  'store-logo': StoreChromeBlocksLazy,
  'store-navigation': StoreChromeBlocksLazy,
  'store-search': StoreChromeBlocksLazy,
  'store-actions': StoreChromeBlocksLazy,
};

for (const type of SIMPLE_BLOCK_TYPES) BLOCK_COMPONENTS[type] = SimpleBlocksLazy;
for (const type of PRODUCT_BLOCK_TYPES) BLOCK_COMPONENTS[type] = ProductBlocksLazy;
for (const type of TICKET_BLOCK_TYPES) BLOCK_COMPONENTS[type] = TicketBlocksLazy;
